/**
 * saas-auth `/api/reseller-admin/**` error copy for the Team, People and Integrations screens
 * (H §5). The codes below get their own message; anything else goes through the shell's
 * `useApiErrorHandler()` (side effects + generic copy, H §4.5). A `429`'s `Retry-After` is
 * appended — 0028's rate-limit handling.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, backendCode } from '@/api/errors';
import { useApiErrorHandler } from '@/components/useApiErrorHandler';

const KNOWN_CODES = new Set([
  'RESELLER_ROLE_NOT_GRANTABLE',
  'RESELLER_LAST_OWNER',
  'INVITATION_ALREADY_PENDING',
  'RESELLER_MEMBER_NOT_INVITABLE',
  'ALREADY_ACTIVE_MEMBER',
  'RESELLER_INVITATION_LIMIT',
  'INVITATION_RESEND_COOLDOWN',
  'GHL_LOCATION_ALREADY_CLAIMED',
  'GHL_LOCATION_NOT_VERIFIED',
]);

/** A rejected saas-auth call as the alert shows it: the message, and the request id to quote. */
export interface MemberError {
  message: string;
  requestId: string | undefined;
}

/** The message and request id for a rejected saas-auth call — runs the shell's side effects too. */
export function useMemberError(): (err: unknown) => MemberError {
  const { t } = useTranslation();
  const handle = useApiErrorHandler();
  return useCallback(
    (err: unknown) => {
      const described = handle(err);
      const code = backendCode(err);
      const requestId = err instanceof ApiError ? err.requestId : undefined;
      if (code === undefined || !KNOWN_CODES.has(code)) {
        return { message: described.message, requestId };
      }
      const message = t(`members.error.${code}`);
      return {
        message:
          described.retryAfterSeconds !== undefined
            ? `${message} ${t('members.error.tryAgainIn', { seconds: described.retryAfterSeconds })}`
            : message,
        requestId,
      };
    },
    [handle, t],
  );
}

/** The message for a rejected saas-auth call — runs the shell's side effects too. */
export function useMemberErrorMessage(): (err: unknown) => string {
  const describe = useMemberError();
  return useCallback((err: unknown) => describe(err).message, [describe]);
}
